import { toSameOriginAssetUrl } from '~/utils/assetUrl'

/**
 * BCA1 密文图片容器解密（资源库成品：url + 64 位 hex key）
 *
 * 容器布局：
 * [0..3]  "BCA1"
 * [4]     version = 1
 * [5..16] IV（12 字节，AES-GCM）
 * [17..]  ciphertext + 16 字节 GCM tag（Web Crypto 一并传入）
 */

const BCA1_MAGIC = new TextEncoder().encode('BCA1')
const BCA1_VERSION = 1
const BCA1_HEADER_LEN = 5
const BCA1_IV_LEN = 12
const BCA1_MIN_LEN = BCA1_HEADER_LEN + BCA1_IV_LEN + 16

type CacheEntry = {
  blobUrl: string
  refs: number
  inflight?: Promise<string>
}

/** url+key → 共享 blob（引用计数，避免列表复用时重复解密） */
const blobCache = new Map<string, CacheEntry>()

function cacheKey(src: string, keyHex: string) {
  return `${src}\0${keyHex}`
}

/** Uint8Array → 独立 ArrayBuffer（满足 Web Crypto BufferSource 严格类型） */
function toArrayBuffer(view: Uint8Array): ArrayBuffer {
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer
}

/** 64 位 hex → 32 字节 AES-256 key */
export function hexToBytes(hex: string): Uint8Array {
  const normalized = hex.trim()
  if (normalized.length % 2 !== 0) {
    throw new Error('bca1: invalid hex key length')
  }
  const out = new Uint8Array(normalized.length / 2)
  for (let i = 0; i < out.length; i++) {
    const byte = Number.parseInt(normalized.slice(i * 2, i * 2 + 2), 16)
    if (Number.isNaN(byte)) throw new Error('bca1: invalid hex key')
    out[i] = byte
  }
  return out
}

function assertBca1Header(buf: Uint8Array): void {
  if (buf.length < BCA1_MIN_LEN) {
    throw new Error('bca1: container too short')
  }
  for (let i = 0; i < 4; i++) {
    if (buf[i] !== BCA1_MAGIC[i]) throw new Error('bca1: bad magic')
  }
  if (buf[4] !== BCA1_VERSION) throw new Error('bca1: unsupported version')
}

/**
 * 解密已下载的 BCA1 容器字节 → 明文图片 ArrayBuffer
 * crypto.subtle 仅在安全上下文（HTTPS / localhost）可用；HTTP+IP 下回退纯 JS AES-GCM
 */
export async function decryptBca1(buf: Uint8Array, keyHex: string): Promise<ArrayBuffer> {
  assertBca1Header(buf)
  const keyBytes = hexToBytes(keyHex)
  const iv = buf.slice(BCA1_HEADER_LEN, BCA1_HEADER_LEN + BCA1_IV_LEN)
  const cipher = buf.slice(BCA1_HEADER_LEN + BCA1_IV_LEN)

  const subtle = globalThis.crypto?.subtle
  if (!subtle) {
    const { gcm } = await import('@noble/ciphers/aes')
    return toArrayBuffer(gcm(keyBytes, iv).decrypt(cipher))
  }

  const aesKey = await subtle.importKey(
    'raw',
    toArrayBuffer(keyBytes),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  )
  return subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv), tagLength: 128 },
    aesKey,
    toArrayBuffer(cipher),
  )
}

/**
 * 解析资源库图片地址。
 * - 无 url → ''（调用方走占位）
 * - 无 key → 明文 url（/content 走同源代理）
 * - 有 key → 下载密文、解密、返回共享 blob: URL（请用 releaseEncryptedImage 释放）
 */
export async function resolveEncryptedImage(
  url?: string | null,
  key?: string | null,
): Promise<string> {
  const raw = (url ?? '').trim()
  if (!raw) return ''

  const src = toSameOriginAssetUrl(raw)
  const keyHex = (key ?? '').trim()
  if (!keyHex) return src

  const id = cacheKey(src, keyHex)
  const hit = blobCache.get(id)
  if (hit?.blobUrl) {
    hit.refs += 1
    return hit.blobUrl
  }
  if (hit?.inflight) {
    const blobUrl = await hit.inflight
    const entry = blobCache.get(id)
    if (entry?.blobUrl) {
      entry.refs += 1
      return entry.blobUrl
    }
    return blobUrl
  }

  const entry: CacheEntry = { blobUrl: '', refs: 0 }
  const inflight = (async () => {
    const res = await fetch(src)
    if (!res.ok) throw new Error(`bca1: fetch failed ${res.status}`)
    const buf = new Uint8Array(await res.arrayBuffer())
    const plain = await decryptBca1(buf, keyHex)
    const blobUrl = URL.createObjectURL(new Blob([plain]))
    const cur = blobCache.get(id)
    if (cur) {
      cur.blobUrl = blobUrl
      cur.inflight = undefined
    }
    return blobUrl
  })()
  entry.inflight = inflight
  blobCache.set(id, entry)

  try {
    const blobUrl = await inflight
    const cur = blobCache.get(id)
    if (cur) cur.refs += 1
    return blobUrl
  } catch (err) {
    blobCache.delete(id)
    throw err
  }
}

/** 释放 resolveEncryptedImage 取得的共享 blob（引用归零时 revoke） */
export function releaseEncryptedImage(url?: string | null, key?: string | null) {
  const raw = (url ?? '').trim()
  const keyHex = (key ?? '').trim()
  if (!raw || !keyHex) return

  const src = toSameOriginAssetUrl(raw)
  const id = cacheKey(src, keyHex)
  const entry = blobCache.get(id)
  if (!entry) return

  entry.refs = Math.max(0, entry.refs - 1)
  if (entry.refs === 0 && !entry.inflight && entry.blobUrl) {
    URL.revokeObjectURL(entry.blobUrl)
    blobCache.delete(id)
  }
}
